import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  ButtonGroup,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { RiDeleteBinLine } from "@remixicon/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { useNavigate } from "react-router";

import AffectedDependencies from "@/common/components/AffectedDependencies";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useCanDeleteFeature } from "@/common/data/useFilterCheck";
import { useErrorToast } from "@/common/hooks/useErrorToast";
import api from "@/common/utils/api";
import { featureQueryKeys } from "@/feature/data/featureQueryKeys";
import { FeatureDetail } from "@reflag/shared/featureAPI";
import { FeaturesUrl } from "@reflag/shared/urls";

export function SettingsDelete({ feature }: { feature?: FeatureDetail }) {
  const { appId, envId, env } = useEnvironment();
  const featureId = feature?.id;

  const toast = useToast();
  const queryClient = useQueryClient();
  const errorToast = useErrorToast();
  const navigate = useNavigate();

  const { mutate: deleteFeature, isPending: isDeleteLoading } = useMutation({
    mutationFn: () => {
      if (!featureId) {
        throw new Error("Cannot delete flag before it has loaded");
      }

      return api.delete<"/apps/:appId/features/:featureId">(
        `/apps/${appId}/features/${featureId}`,
      );
    },

    retry: 0,

    onSuccess: async () => {
      toast({
        title: "Flag deleted",
        status: "success",
      });
      queryClient.invalidateQueries({
        queryKey: featureQueryKeys.listNames(appId),
      });
      queryClient.cancelQueries({
        queryKey: featureQueryKeys.single(appId, envId, featureId),
      });
      queryClient.invalidateQueries({
        queryKey: featureQueryKeys.list(appId, envId),
      });
      navigate(FeaturesUrl(env));
    },

    onError: () => {
      errorToast({
        title: "Couldn't delete flag",
      });
    },
  });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { canDelete, isLoading: loadingCanDelete } = useCanDeleteFeature(
    isOpen ? featureId : undefined,
  );

  const cancelRef = useRef(null);

  const buttonTextColor = useColorModeValue("red.500", "red.400");

  return (
    <>
      <Button
        color={buttonTextColor}
        isDisabled={isDeleteLoading || !featureId}
        leftIcon={<RiDeleteBinLine size={16} />}
        loadingText="Deleting..."
        type="submit"
        variant="outline"
        onClick={onOpen}
      >
        Delete flag...
      </Button>
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Delete flag</AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete{" "}
              <strong>{feature?.name ?? "this flag"}</strong>?
              <Text mt={4}>
                Deleting a flag will cause its flag to always evaluate to{" "}
                <strong>disabled</strong> and its remote config will no longer
                be available.
              </Text>
              <Text mt={2}>
                Any user feedback for this flag will be deleted as well. You
                can&apos;t undo this.
              </Text>
              {isOpen && featureId && (
                <AffectedDependencies
                  changeType="delete"
                  dependee={{
                    type: "feature",
                    id: featureId,
                  }}
                  showAffectEnvironments
                />
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <ButtonGroup>
                <Button
                  ref={cancelRef}
                  isDisabled={isDeleteLoading}
                  variant="ghostDimmed"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  isDisabled={!canDelete}
                  isLoading={isDeleteLoading || loadingCanDelete}
                  loadingText={isDeleteLoading ? "Deleting" : undefined}
                  onClick={() => deleteFeature()}
                >
                  Delete
                </Button>
              </ButtonGroup>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
